import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  experienceStorage,
  STORAGE_KEYS,
} from '@experiences/blocs/utils/storage';
import {
  SALES_PORTAL_ROUTES,
  SALES_PORTAL_TAB_ROUTES,
} from '@/navigators/routes';

type SalesPortalLandingRoute =
  | SALES_PORTAL_TAB_ROUTES
  | SALES_PORTAL_ROUTES.Home;

const TAB_LANDING_ROUTES = new Set<string>(
  Object.values(SALES_PORTAL_TAB_ROUTES),
);

/**
 * Maps the active top-level route to the landing route persisted for the next
 * visit. Named tabs persist as themselves. Everything else — the microproduct
 * Base route, Home, and standalone screens — persists as Home, whose screen
 * re-derives the landing target from the current sidebar config (first nav
 * item). Persisting a concrete tab for those would send orgs whose sidebar
 * has no such tab (e.g. manufacturer orgs with only a microproduct) into a
 * screen they can't otherwise reach.
 */
export const toLandingRoute = (routeName: string): SalesPortalLandingRoute =>
  TAB_LANDING_ROUTES.has(routeName)
    ? (routeName as SALES_PORTAL_TAB_ROUTES)
    : SALES_PORTAL_ROUTES.Home;

interface SalesPortalNavigationState {
  lastActiveRoute?: SalesPortalLandingRoute;
}

interface SalesPortalNavigationActions {
  setLastActiveRoute: (route: SalesPortalLandingRoute) => void;
}

type SalesPortalNavigationStore = SalesPortalNavigationState &
  SalesPortalNavigationActions;

export const useSalesPortalNavigationStore =
  create<SalesPortalNavigationStore>()(
    persist(
      (set) => ({
        lastActiveRoute: undefined,
        setLastActiveRoute: (route) => set({ lastActiveRoute: route }),
      }),
      {
        migrate: () => ({ lastActiveRoute: undefined }),
        name: STORAGE_KEYS.NAVIGATION_STATE,
        storage: createJSONStorage(() => ({
          getItem: (key) => experienceStorage.getString(key) ?? null,
          removeItem: (key) => experienceStorage.remove(key),
          setItem: (key, value) => experienceStorage.set(key, value),
        })),
        // v0's handler wrote OrderEntry on every non-Pricing navigation, so
        // every device that used the portal before v1 has a poisoned value
        // persisted. Clearing it on migrate forces one clean Home-derived
        // landing; the store then repopulates with real values.
        version: 1,
      },
    ),
  );
